import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useRouteMatch, Route, Switch } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { useAPIError } from '../../../context';
import { Error404 } from '../..';
import { AdminCharityBreadCrumb, AdminUserTitle } from '../shared';
import { getOneCharity } from '../service';
import AdminUserDonations from '../adminUserDetails/AdminUserDonations';
import AdminCharityDetailsCards from './AdminCharityDetailsCards';

const AdminCharityDetails = ({ t }) => {
    const { addError } = useAPIError();
    const { accountId } = useParams();
    const { path } = useRouteMatch();

    const [isLoading, setIsLoading] = useState(true);
    const [accountData, setAccountData] = useState();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    /**
     * GET request to API to get charity's account informations
     * @returns {object} - object with charity's infos for account
     */
    const getCharityAccount = useCallback(() => {
        isMounted.current && setIsLoading(true);

        getOneCharity(accountId)
            .then((res) => {
                if (isMounted.current) {
                    setAccountData(res.data);
                }
            })
            .catch((error) => {
                const { status, detail } = error?.response?.data;
                addError(detail, status);
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    }, [addError, accountId]);

    useEffect(() => {
        isMounted.current = true;

        getCharityAccount();

        return () => {
            isMounted.current = false;
        };
    }, [getCharityAccount]);

    return (
        <>
            <Helmet>
                <title>
                    {t('admin.user.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <AdminCharityBreadCrumb accountId={accountId} />

            <AdminUserTitle
                organisationName={accountData?.association?.name}
                accountId={accountId}
                isLoading={isLoading}
            />

            <Switch>
                <Route exact path={`${path}`}>
                    <AdminCharityDetailsCards
                        setIsLoading={setIsLoading}
                        accountData={accountData}
                        setAccountData={setAccountData}
                        isLoading={isLoading}
                        refresh={getCharityAccount}
                    />
                </Route>

                <Route exact path={`${path}/charity-donations`}>
                    <AdminUserDonations
                        accountId={accountId}
                        idAssociation={accountData?.association?.idAssociation}
                    />
                </Route>

                <Route component={Error404} />
            </Switch>
        </>
    );
};

export default withTranslation('common')(AdminCharityDetails);
